import { useState } from "react";
import { Info } from "lucide-react";
import { Checkbox } from "~/components/ui/checkbox";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import { FormDialog } from "~/pages/manifold-nextgen/components/FormDialog";
import { useAsyncMutation } from "~/pages/manifold-nextgen/hooks/useAsyncMutation";
import {
  EVAL_CASES_API,
  EVAL_SCORE_MAX,
  type EvalCase,
  type EvalCaseCreate,
} from "~/services/evals";
import { apiMutate } from "~/utils/apiClient";
import { ASSERTION_TOGGLES, JUDGE_TOGGLES } from "./constants/evaluators";
import { POPOVER_IN_DIALOG_Z } from "./constants/page";
import { Field } from "./primitives/layout";

/** A dataset as the catalog list returns it, narrowed to what the picker needs. */
export interface DatasetOption {
  id: string;
  title: string;
}

interface CaseFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  datasets: DatasetOption[];
  /** Suite tags already in use, offered so a new case joins an existing suite. */
  availableTags: string[];
  onCreated: () => void;
}

/**
 * Creates an eval case, shaped by what a run needs rather than by the API's JSONB
 * columns. `inputs` and `expectations` are assembled on submit through the same
 * mapping the YAML importer performs, so a case created here is indistinguishable
 * from an imported one.
 *
 * Judges and assertions are separated because they do different things to the
 * result: a judge's score is what `score` averages, an assertion only flips
 * `passed`, and a case of assertions alone records no score — which the form says
 * rather than letting it be found in an empty column. At least one is required.
 */
export const CaseFormDialog = ({
  open,
  onOpenChange,
  datasets,
  availableTags,
  onCreated,
}: CaseFormDialogProps) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [query, setQuery] = useState("");
  const [datasetId, setDatasetId] = useState("");
  const [suite, setSuite] = useState("");
  const [annotation, setAnnotation] = useState("");
  const [expectedAnswer, setExpectedAnswer] = useState("");
  const [evaluators, setEvaluators] = useState<string[]>(["evaluate_text_response"]);
  const [skillName, setSkillName] = useState("");
  const [dataframeCount, setDataframeCount] = useState("");
  const [threshold, setThreshold] = useState("");
  const [maxLatency, setMaxLatency] = useState("");

  const wantsSkillAssertion = evaluators.includes("evaluate_skill_invocation");
  const hasJudge = JUDGE_TOGGLES.some((toggle) => evaluators.includes(toggle.key));

  const parsedThreshold = threshold.trim() === "" ? null : Number(threshold);
  const parsedLatency = maxLatency.trim() === "" ? null : Number(maxLatency);
  const parsedDataframeCount = dataframeCount.trim() === "" ? null : Number(dataframeCount);
  const isThresholdValid = parsedThreshold == null || Number.isFinite(parsedThreshold);
  const isLatencyValid =
    parsedLatency == null || (Number.isInteger(parsedLatency) && parsedLatency > 0);
  const isDataframeCountValid =
    parsedDataframeCount == null ||
    (Number.isInteger(parsedDataframeCount) && parsedDataframeCount >= 0);
  // The runner raises rather than skipping when `evaluate_skill_invocation` has
  // no `skill_name` to assert against, so the coupling is enforced here — the
  // alternative is a case that fails the whole run the first time it is picked up.
  const isSkillNameValid = !wantsSkillAssertion || skillName.trim().length > 0;
  const isValid =
    name.trim().length > 0 &&
    query.trim().length > 0 &&
    datasetId !== "" &&
    suite.trim().length > 0 &&
    evaluators.length > 0 &&
    isThresholdValid &&
    isLatencyValid &&
    isDataframeCountValid &&
    isSkillNameValid;

  const reset = () => {
    setName("");
    setDescription("");
    setQuery("");
    setDatasetId("");
    setSuite("");
    setAnnotation("");
    setExpectedAnswer("");
    setEvaluators(["evaluate_text_response"]);
    setSkillName("");
    setDataframeCount("");
    setThreshold("");
    setMaxLatency("");
  };

  const { execute, isPending } = useAsyncMutation(
    async () => {
      const expectations: Record<string, unknown> = Object.fromEntries(
        evaluators.map((key) => [key, true])
      );
      // Only sent when there is one: an empty `expected_answer` would give the
      // text judge nothing to compare against while still looking configured.
      if (expectedAnswer.trim()) {
        expectations.expected_answer = expectedAnswer.trim();
      }
      if (wantsSkillAssertion) {
        expectations.skill_name = skillName.trim();
      }
      // A count of zero is a real expectation — "answer this without producing a
      // dataframe" — so the key is written whenever the field was filled in,
      // rather than when it is truthy.
      if (parsedDataframeCount != null) {
        expectations.expected_dataframe_count = parsedDataframeCount;
      }

      const body: EvalCaseCreate = {
        name: name.trim(),
        description: description.trim() || null,
        // Suite first, matching the YAML importer's ordering — a run's tag
        // selector is containment, so the suite tag is what scopes a run.
        tags: [suite.trim(), ...(annotation.trim() ? [annotation.trim()] : [])],
        dataset_ids: [datasetId],
        inputs: { query: query.trim() },
        expectations,
        pass_threshold: parsedThreshold,
        max_latency_seconds: parsedLatency,
      };
      return apiMutate<EvalCase>(EVAL_CASES_API, "POST", body);
    },
    {
      errorMessage: "Could not create the case.",
      onSuccess: () => {
        reset();
        onCreated();
        onOpenChange(false);
      },
    }
  );

  const toggleEvaluator = (key: string) =>
    setEvaluators((current) =>
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key]
    );

  return (
    <FormDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          reset();
        }
        onOpenChange(next);
      }}
      title="New eval case"
      description="A question, the dataset to ask it against, and what the judges should check."
      contentClassName="sm:max-w-2xl"
      primaryLabel="Create case"
      primaryPendingLabel="Creating…"
      primaryDisabled={!isValid}
      isPending={isPending}
      onPrimary={() => void execute()}
    >
      <div className="my-2 max-h-[60vh] overflow-y-auto pr-1">
        <div className="flex flex-col gap-5">
          <Field
            label="Name"
            htmlFor="new-case-name"
            hint="Snake case, matching the suite files — it is how the case is cited everywhere else."
          >
            <Input
              id="new-case-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="count_lung_cancer_cases"
              className="font-mono text-xs"
            />
          </Field>

          <Field
            label="Question"
            htmlFor="new-case-query"
            hint="Put to the agent verbatim, exactly as a user would type it."
          >
            <Textarea
              id="new-case-query"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              rows={3}
              placeholder="How many patients in the LUAD cohort have a KRAS mutation?"
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Dataset"
              htmlFor="new-case-dataset"
              hint="The run resolves the agent's data access from this."
            >
              <Select value={datasetId} onValueChange={setDatasetId}>
                <SelectTrigger id="new-case-dataset" className="w-full">
                  <SelectValue placeholder="Select a dataset…" />
                </SelectTrigger>
                <SelectContent className={POPOVER_IN_DIALOG_Z}>
                  {datasets.map((dataset) => (
                    <SelectItem key={dataset.id} value={dataset.id}>
                      {dataset.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <Field
              label="Suite"
              htmlFor="new-case-suite"
              hint="Runs select cases by tag, so this decides which runs cover the case."
            >
              <Input
                id="new-case-suite"
                value={suite}
                onChange={(event) => setSuite(event.target.value)}
                placeholder="TCGA Data Retrieval"
                list="new-case-suite-options"
              />
              <datalist id="new-case-suite-options">
                {availableTags.map((tag) => (
                  <option key={tag} value={tag} />
                ))}
              </datalist>
            </Field>
          </div>

          <Field
            label="Annotation"
            htmlFor="new-case-annotation"
            hint="A second tag grouping like cases together — counts, survival, figures."
          >
            <Input
              id="new-case-annotation"
              value={annotation}
              onChange={(event) => setAnnotation(event.target.value)}
              placeholder="counts"
            />
          </Field>

          <div className="grid gap-5 sm:grid-cols-2">
            <ToggleGroup
              legend={`Judges (score 1-${EVAL_SCORE_MAX})`}
              caption="One model call per judge, per iteration. Their scores set the case's score."
              toggles={JUDGE_TOGGLES}
              selected={evaluators}
              onToggle={toggleEvaluator}
            />
            <ToggleGroup
              legend="Assertions (pass or fail)"
              caption="Free to run. They never affect the score; a false one only fails the case."
              toggles={ASSERTION_TOGGLES}
              selected={evaluators}
              onToggle={toggleEvaluator}
            />
          </div>

          {evaluators.length === 0 && (
            <p className="text-xs text-red-700">
              Turn on at least one. A case with none runs, spends agent time and produces no
              judgement.
            </p>
          )}
          {evaluators.length > 0 && !hasJudge && (
            <p className="flex items-start gap-1.5 text-xs text-orange-700">
              <Info className="mt-px size-3.5 shrink-0" aria-hidden />
              Assertions only, so this case will record no score — it will pass or fail, and sit
              outside the score bands on the case-health grid.
            </p>
          )}

          {wantsSkillAssertion && (
            <Field
              label="Skill name"
              htmlFor="new-case-skill"
              hint="The skill that must be the one the agent reaches for, named exactly as the skill directory is."
            >
              <Input
                id="new-case-skill"
                value={skillName}
                onChange={(event) => setSkillName(event.target.value)}
                placeholder="kaplan_meier"
                className="font-mono text-xs"
                aria-invalid={!isSkillNameValid || undefined}
              />
            </Field>
          )}

          <Field
            label="Expected answer"
            htmlFor="new-case-expected"
            hint="What a correct response should arrive at. The text and value judges score against it."
          >
            <Input
              id="new-case-expected"
              value={expectedAnswer}
              onChange={(event) => setExpectedAnswer(event.target.value)}
              placeholder="585 patients"
            />
          </Field>

          <Field
            label="Expected dataframe count"
            htmlFor="new-case-dataframes"
            hint="Asserts how many dataframes the run should produce. Blank asserts nothing; zero asserts none."
          >
            <Input
              id="new-case-dataframes"
              type="number"
              min={0}
              value={dataframeCount}
              onChange={(event) => setDataframeCount(event.target.value)}
              placeholder="Not asserted"
              aria-invalid={!isDataframeCountValid || undefined}
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label="Pass threshold"
              htmlFor="new-case-threshold"
              hint="Blank means assertions alone decide pass or fail."
            >
              <Input
                id="new-case-threshold"
                type="number"
                step="0.1"
                min={1}
                max={4}
                value={threshold}
                onChange={(event) => setThreshold(event.target.value)}
                placeholder="None"
                aria-invalid={!isThresholdValid || undefined}
              />
            </Field>
            <Field
              label="Latency limit (s)"
              htmlFor="new-case-latency"
              hint="Adds a latency assertion: a run slower than this fails the case, however good the answer."
            >
              <Input
                id="new-case-latency"
                type="number"
                min={1}
                value={maxLatency}
                onChange={(event) => setMaxLatency(event.target.value)}
                placeholder="Unbounded"
                aria-invalid={!isLatencyValid || undefined}
              />
            </Field>
          </div>

          <p className="flex items-start gap-1.5 rounded-md bg-muted/60 p-3 text-xs text-muted-foreground">
            <Info className="mt-px size-3.5 shrink-0" aria-hidden />
            The case is created at version 1 and covered by the next run whose tags include the
            suite above. Creating it does not launch anything.
          </p>
        </div>
      </div>
    </FormDialog>
  );
};

/**
 * One kind of evaluator, as a checkbox column. Both groups write into the same
 * `evaluators` list — the split is for the reader, not the payload.
 */
const ToggleGroup = ({
  legend,
  caption,
  toggles,
  selected,
  onToggle,
}: {
  legend: string;
  caption: string;
  toggles: readonly { key: string; label: string; hint: string }[];
  selected: string[];
  onToggle: (key: string) => void;
}) => (
  <fieldset className="flex flex-col gap-1.5">
    <legend className="text-sm font-medium text-foreground">{legend}</legend>
    <p className="text-xs text-muted-foreground">{caption}</p>
    <div className="mt-1 flex flex-col gap-2">
      {toggles.map((toggle) => (
        <div key={toggle.key} className="flex items-start gap-2">
          {/* `onCheckedChange`, not `onChange`: this is a Radix checkbox, which
              renders a button and never emits a change event. And an explicit
              `htmlFor` rather than a wrapping label, because a label only
              activates the control it names — wrapping a Radix checkbox in one
              leaves the text dead to the pointer. */}
          <Checkbox
            id={`new-case-${toggle.key}`}
            checked={selected.includes(toggle.key)}
            onCheckedChange={() => onToggle(toggle.key)}
            className="mt-0.5 size-4"
          />
          <Label
            htmlFor={`new-case-${toggle.key}`}
            className="cursor-pointer flex-col items-start gap-0 text-sm font-normal text-foreground"
          >
            {toggle.label}
            <span className="text-xs text-muted-foreground">{toggle.hint}</span>
          </Label>
        </div>
      ))}
    </div>
  </fieldset>
);
